import { useEffect, useState } from 'react'
import { Cpu, HardDrive, MemoryStick, MonitorCog, RefreshCw } from 'lucide-react'
import { api } from '@/services/api'
import type { DeviceCapabilities } from '@/services/types'

/**
 * What this machine is, shown above the model list.
 *
 * Recommendations are only trustworthy if the user can see what they were
 * based on — a verdict with no visible inputs reads as a guess.
 */
export function DeviceCapabilityCard() {
  const [device, setDevice] = useState<DeviceCapabilities | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    let cancelled = false
    void api
      .deviceCapabilities()
      .then((d) => {
        if (!cancelled) setDevice(d)
      })
      // Hardware detail is helpful, not essential; failing quietly beats an
      // error banner above a page that otherwise works.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const refresh = async () => {
    setRefreshing(true)
    try {
      setDevice(await api.refreshDeviceCapabilities())
    } catch {
      // Keep whatever we already showed.
    } finally {
      setRefreshing(false)
    }
  }

  if (!device || device.totalMemoryBytes <= 0) return null

  const gpu = device.gpus.find((g) => g.vramBytes && g.vramBytes > 0) ?? device.gpus[0]

  return (
    <div className="mb-5 rounded-lg border border-border bg-surface-raised p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-content-muted">This machine</span>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          title="Re-check after closing other applications"
          className="flex items-center gap-1 text-[11px] text-content-muted hover:text-content disabled:opacity-40"
        >
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : undefined} />
          Re-check
        </button>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
        <Stat icon={Cpu} label={device.cpuModel ?? `${device.cpuCores} cores`}>
          {device.cpuCores} cores
        </Stat>

        <Stat icon={MemoryStick} label="Memory available now">
          {formatBytes(device.availableMemoryBytes)} free of {formatBytes(device.totalMemoryBytes)}
        </Stat>

        {gpu && (
          <Stat icon={MonitorCog} label={gpu.name}>
            {gpu.name}
            {gpu.vramBytes ? ` · ${formatBytes(gpu.vramBytes)}` : ''}
            {device.unifiedMemory ? ' · shared memory' : ''}
          </Stat>
        )}

        <Stat icon={HardDrive} label="Space for model downloads">
          {formatBytes(device.freeDiskBytes)} free
        </Stat>
      </div>
    </div>
  )
}

function Stat({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Cpu
  label: string
  children: React.ReactNode
}) {
  return (
    <span className="flex items-center gap-1.5 text-content-muted" title={label}>
      <Icon size={13} className="shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  )
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—'
  const gb = bytes / 1_000_000_000
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  return `${Math.round(bytes / 1_000_000)} MB`
}
